import { H6 } from "@sellar-io/components";

import { OrderGetResponseType } from "@sellar-io/api-v2/types";

import dayjs from "dayjs";

import { ReactElement } from "react";

import { OrderStage } from "../helpers";
import { SellarCreditPaymentStatusText } from "./SellarCreditPaymentStatusText";

export type OrderPaymentStatusProps = {
  paymentDate?: Date | null;
  paymentStatus: OrderGetResponseType["paymentStatus"];
  paymentTerms?: "credit" | "proforma" | "paymentOnDelivery" | null;
  orderStage: OrderStage;
  stripeInvoiceId?: string | null;
  invoiceCreationStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  confirmedAt: Date | null;
  orderPrice: number;
  paymentType: OrderGetResponseType["paymentType"];
  hasPaymentLink: boolean;
};

export type SellarCreditPaymentStatusTextProps = {
  orderStage: OrderPaymentStatusProps["orderStage"];
};

export const generateOrderPaymentStatusText = ({
  paymentDate,
  paymentStatus,
  paymentTerms,
  orderStage,
  stripeInvoiceId,
  invoiceCreationStage,
  confirmedAt,
  confirmationText,
  orderPrice,
  paymentType,
  hasPaymentLink,
}: OrderPaymentStatusProps & {
  confirmationText: string;
}): ReactElement | null => {
  const issuedOn =
    paymentTerms === "paymentOnDelivery"
      ? "finalisation"
      : invoiceCreationStage === "delivered"
        ? "delivery"
        : invoiceCreationStage === "dispatched"
          ? "dispatch"
          : confirmationText;

  if (paymentType === "sellarCredit") {
    return <SellarCreditPaymentStatusText orderStage={orderStage} />;
  }

  if (orderPrice === 0) {
    return <H6>No payment due</H6>;
  }

  if (paymentStatus === "refunded") {
    return <H6>Refunded</H6>;
  }

  if (orderStage === "Cancelled") {
    return <H6>Order cancelled</H6>;
  }

  if (orderStage === "Voided") {
    return <H6>Order voided</H6>;
  }

  if (paymentStatus === "paid") {
    return <H6>Paid on {dayjs(paymentDate).format("DD/MM/YYYY")}</H6>;
  }

  if (hasPaymentLink && !paymentDate) {
    return <H6>Payment requested</H6>;
  }

  if (stripeInvoiceId && !paymentDate) {
    return <H6>Payment due today</H6>;
  }

  if (!paymentDate) {
    if (!confirmedAt) {
      return <H6>Not final - awaiting supplier {confirmationText}.</H6>;
    }

    return <H6>Not final - invoice issued on {issuedOn} of order.</H6>;
  }

  const now = Date.now();

  if (dayjs(paymentDate).isSame(now, "day")) {
    return <H6>Payment due now</H6>;
  }

  if (dayjs(paymentDate).isAfter(now, "day")) {
    return (
      <H6>
        Payment due by{" "}
        {dayjs(paymentDate).subtract(1, "hour").format("DD/MM/YYYY")}
      </H6>
    );
  }

  if (dayjs(paymentDate).isBefore(now, "day")) {
    return (
      <H6 className="font-bold text-red-500">
        Payment overdue by {dayjs(now).diff(paymentDate, "days")} days
      </H6>
    );
  }

  return null;
};
